import React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import Stock from '../../model/Stock';
import FormTextItem from '../form/FormTextItem';
import FormSwitchItem from '../form/FormSwitchItem';
import { ShareRegistryProvider } from '../../model/ShareRegistryProvider';
import { useSelector } from 'react-redux';
import { IGlobalState } from '../../model/IGlobalState';
import * as Roles from '../../model/Roles';

// the 3 steps from the "Automic Setup" google doc
const showAutomicSetupHelp = () => {
    confirmAlert({
        customUI: ({ onClose }) => (
            <div className='custom-ui'>
                <h3>Generate an API Key in Automic</h3>
                <ol style={{textAlign: 'left', paddingLeft: '1.2rem'}}>
                    <li style={{marginBottom: '0.75rem'}}>
                        Go to <b>API Management</b> under <b>Settings</b> and select <b>Create APP</b>.<br/>
                        <small>If instead you see &ldquo;You don&apos;t have permissions to manage OAuth Apps&rdquo; you will need to contact Automic and have it enabled.</small>
                    </li>
                    <li style={{marginBottom: '0.75rem'}}>
                        Provide the App name, App Description &amp; Scopes (select all read-only).
                    </li>
                    <li>
                        Once the application is created, the Client Id and API Key are displayed on the APP panel.
                        Click <b>Generate secret</b> to obtain the Client Secret.
                    </li>
                </ol>
                <button onClick={onClose}>Ok</button>
            </div>
        )
    });
}

interface IProps {
    stock: Stock;
    ticker: string;
    exchange: string;
    onChange: (e:React.ChangeEvent<HTMLInputElement>) => void;
    onConfigDataChange: (name:string, value:string|boolean|number|object) => void;
}

const ShareRegistryConfig = (props:IProps) => {
    const { stock, onChange, onConfigDataChange } = props;
    const user = useSelector((state: IGlobalState) => state.user);
    const isAdmin = user.roles.hasOwnProperty(Roles.Admin);

    // .form-item has a 25px bottom margin, match it on the non form-item children so
    // each row's fields bottom-align.
    return <div style={{background: '#f6f9fc', padding: '1rem 0.5rem', borderRadius: '4px', marginTop: '0.5rem'}}>
        <h4 style={{margin: '0 0 8px'}}>Share Registry</h4>
        <div style={{marginBottom: '25px'}}>
            <label>Provider</label>
            <select
                value={stock.shareRegistryProvider ?? ShareRegistryProvider.None}
                name="shareRegistryProvider"
                onChange={(e) => { onConfigDataChange(e.target.name, parseInt(e.target.value)) }}
                style={{marginLeft: '8px', maxWidth: '300px'}}>
                <option value={ShareRegistryProvider.None}>None</option>
                <option value={ShareRegistryProvider.Automic}>Automic</option>
                <option value={ShareRegistryProvider.Xcend}>Xcend</option>
                <option value={ShareRegistryProvider.Computershare}>Computershare</option>
                <option value={ShareRegistryProvider.MUFG}>MUFG</option>
                <option value={ShareRegistryProvider.Advanced}>Advanced</option>
                <option value={ShareRegistryProvider.Boardroom}>Boardroom</option>
            </select>
        </div>
        {stock.shareRegistryProvider === ShareRegistryProvider.Automic && <>
            <FormTextItem label="API Key" name='automicApiKey' value={stock.automicApiKey!} onChange={onChange}
                helperText={!stock.automicApiKey ? <span className="link" onClick={showAutomicSetupHelp}>Where do I get these details?</span> : undefined}/>
            <div style={{display: 'flex', gap: '24px', flexWrap: 'wrap'}}>
                <div style={{flex: '1 1 300px'}}>
                    <FormTextItem label="Client Id" name='automicClientId' value={stock.automicClientId!} onChange={onChange}/>
                </div>
                <div style={{flex: '1 1 300px'}}>
                    <FormTextItem label="Client Secret" name='automicClientSecret' value={stock.automicClientSecret!} onChange={onChange}/>
                </div>
            </div>
        </>}
        {stock.shareRegistryProvider === ShareRegistryProvider.Xcend && <div style={{maxWidth: '300px'}}
            title="Xcend uses one API key for all clients; the stock must also be approved by Xcend before lookups work">
            <FormSwitchItem label="Enabled" name="xcendIsEnabled" value={!!stock.xcendIsEnabled} disabled={!isAdmin}
                onChange={(name, value) => onConfigDataChange(name, value)}>
                {!isAdmin && <small>Admin only</small>}
            </FormSwitchItem>
        </div>}
    </div>
}

export default ShareRegistryConfig;
